// Pure DERIVATIONS for the Work landing surface (brief §5.1): the stat-strip figures and each
// definition row's sweep-cell rendering model. Every value derives from the landing projection the
// owning module returned (G-182) — nothing here invents a count or a health state, and the paused /
// pre-first-sweep cases render honestly (a null lastSweep is "first sweep pending", never a made-up
// stamp — brief §5.2 honest states). Kept out of the components for the size ceilings (G-085).

import type { WorkLandingDefinitionRow } from '../projections.ts';

/** The landing stat-strip figures, derived from the definitions the projection lists. */
export interface LandingStats {
  readonly activeCount: number;
  readonly pausedCount: number;
  /** The needs-a-decision total across all boards. */
  readonly needsDecision: number;
  /** The first active definition's due sweep (projection order); null when none is scheduled. */
  readonly nextSweep: { readonly at: string; readonly cadenceLabel: string } | null;
}

/** Derive the stat strip from the definitions list — numbers are computed, never hand-kept. */
export function deriveLandingStats(rows: readonly WorkLandingDefinitionRow[]): LandingStats {
  const active = rows.filter((row) => row.state === 'active');
  const firstDue = active.find((row) => row.nextDueAt !== null);
  return {
    activeCount: active.length,
    pausedCount: rows.length - active.length,
    needsDecision: rows.reduce((sum, row) => sum + row.needsDecision, 0),
    nextSweep:
      firstDue !== undefined && firstDue.nextDueAt !== null
        ? { at: firstDue.nextDueAt, cadenceLabel: firstDue.cadenceLabel }
        : null,
  };
}

/** The sweep cell's machine state (rides `data-sweep-health` so tests assert behaviour, G-180). */
export type SweepHealth = 'ok' | 'attention' | 'paused' | 'pending';

/** One definition row's sweep cell, resolved for rendering. */
export interface SweepCell {
  readonly health: SweepHealth;
  readonly label: string;
  readonly sub: string;
}

/** Resolve a row's sweep cell from its lifecycle state and last-sweep stamp (G-182). */
export function deriveSweepCell(row: WorkLandingDefinitionRow): SweepCell {
  if (row.state === 'paused') {
    return { health: 'paused', label: row.lastSweep?.at ?? 'Paused', sub: 'paused' };
  }
  if (row.lastSweep === null) {
    return {
      health: 'pending',
      label: row.nextDueAt !== null ? `First sweep ${row.nextDueAt}` : 'First sweep pending',
      sub: `${row.cadenceLabel} sweep`,
    };
  }
  if (row.lastSweep.outcome === 'completed') {
    return { health: 'ok', label: row.lastSweep.at, sub: `${row.cadenceLabel} sweep` };
  }
  return {
    health: 'attention',
    label: row.lastSweep.at,
    sub: row.lastSweep.outcome === 'failed' ? 'sweep failed' : 'partial sweep',
  };
}
